/**
 * Parent-aware back navigation.
 *
 * The default `navigate(-1)` follows the BROWSER history stack — every
 * page you visited is replayed in reverse. After 5 hops, you tap "back"
 * 5 times to reach Home. In a Telegram WebApp where users hop between
 * features non-linearly (Home → Master → Diary → Profile), that's
 * exhausting.
 *
 * Instead we define each route's LOGICAL parent. "Back" goes to that
 * parent, not whichever screen happened to be visited last. Almost every
 * feature returns to Home directly; only sub-pages (e.g. a specific
 * spread inside Spreads list) return to their immediate parent.
 *
 * Tip: when adding a new route, add it here too — otherwise back will
 * fall through to `navigate(-1)` (the old behaviour).
 */

/** Map: route pattern → its logical parent. Order matters for matching:
 *  more specific patterns (with params) come first.
 *
 * Rule of thumb: a route's parent is the screen that LINKS TO IT in the
 * UI. If only Spreads.tsx links to /dream, then back from /dream goes
 * to /spreads — not to Home. Otherwise back jumps over the spreads
 * catalog and confuses the user. */
const PARENT_MAP: Array<[RegExp, string]> = [
  // Sub-routes with parameters: a specific spread / compat invite belongs
  // to its listing page.
  [/^\/reading\/[^/]+$/, '/spreads'],
  [/^\/compat\/[^/]+$/, '/compat'],

  // Features reachable ONLY from the spreads catalog — back returns there,
  // not all the way to Home. Bug fix: previously these all went to '/',
  // which felt jarring after deep navigation through Spreads.
  [/^\/dream$/, '/spreads'],
  [/^\/themed$/, '/spreads'],
  [/^\/numerology$/, '/spreads'],
  [/^\/weekly$/, '/spreads'],

  // Features linked from Home directly — back goes to Home.
  [/^\/spreads$/, '/'],
  [/^\/yes-no$/, '/'],
  [/^\/name-compat$/, '/'],
  [/^\/angel$/, '/'],
  [/^\/journal$/, '/'],
  [/^\/compat$/, '/'],
  [/^\/master$/, '/'],
  [/^\/rituals$/, '/'],
  [/^\/diary$/, '/'],
  [/^\/profile$/, '/'],
  [/^\/natal-chart$/, '/'],
  [/^\/synastry$/, '/'],
];

/** Returns the parent route for the given pathname, or `'/'` as a safe
 *  default if nothing matches (so back always works somehow). */
export function parentRoute(pathname: string): string {
  for (const [pattern, parent] of PARENT_MAP) {
    if (pattern.test(pathname)) return parent;
  }
  return '/';
}
